import {Location, History} from "history";
import {Col, DSSpinner} from "@cdx/ds";
import {Root} from "../../../../cdx-models/Root";
import {Card} from "../../../../cdx-models/Card";
import useSet from "../../../../lib/hooks/useSet";
import ConversationHeader from "./ConversationHeader";
import {useEffect, useRef, useState} from "react";
import {checkIfPresent} from "../../../../lib/mate/mate-utils";
import {api} from "../../../../lib/api";
import {Resolvable, ResolvableId} from "../../../../cdx-models/Resolvable";
import {RevealSpinner} from "@cdx/common";
import NewThread from "./NewThread";
import ConversationThread from "./ConversationThread";
import {createConvoBag} from "./convo-utils";
import {orderBy} from "lodash";
import {Notifications} from "../../../notifications/useNotifications";

const WithLoadedResolvables = (props: {resolvables: Resolvable[]} & NextConversationsProps) => {
  const {
    forceThreadType,
    resolvables,
    card,
    onForceThreadCreated,
    onReceiveFile,
    root,
    canModify,
    status,
    onCardDone,
    location,
    history,
    onEditCardClick,
    notifications,
  } = props;

  const [justClosedIds, justClosedIdsActions] = useSet<ResolvableId>();

  const openResolvables = resolvables.filter((r) => !r.isClosed);

  const [createCommentThreadMode, setCreateCommentThreadMode] = useState<
    null | "auto" | "review" | "block" | "explicit"
  >(!forceThreadType && openResolvables.length === 0 ? "auto" : null);

  const createThreadType = forceThreadType || (createCommentThreadMode ? "comment" : null);

  const [activeFilters, activeFiltersActions] = useSet<string>();

  const shownResolvables = activeFilters.size
    ? resolvables.filter((r) => activeFilters.has(r.context))
    : resolvables;

  const orderedResolvables = orderBy(shownResolvables, (r) =>
    r.isClosed && !justClosedIds.has(r.id as ResolvableId) ? 1 : -1
  );

  const onScrollToTarget = () => {
    const {cardTargetResolvableId, ...restState} = location.state || ({} as any);
    history.replace({...location, state: restState});
  };

  const bag = createConvoBag({
    root,
    card,
    status,
    onReceiveFile,
    canModify,
    onReopen: (resId) => justClosedIdsActions.remove(resId),
    onCloseThread: (resId) => justClosedIdsActions.add(resId),
    onCardDone,
    location,
    lastClosedResolvableId: [...justClosedIds].slice(-1)[0],
    onEditCardClick,
    onScrollToTarget,
    notifications,
  });

  return (
    <Col sp="16px" px="16px" pt="12px" pb="32px" data-cdx-context="Conversations">
      <ConversationHeader
        activeFilters={activeFilters}
        activeFiltersActions={activeFiltersActions}
        resolvables={resolvables}
        toggleNewThread={() => setCreateCommentThreadMode((v) => (v ? null : "explicit"))}
        newThreadType={createThreadType}
      />
      <Col sp="48px">
        {createThreadType && process.env.REACT_APP_MODE !== "open" && (
          <NewThread
            type={createThreadType}
            bag={bag}
            onCreateNewThread={() => {
              setCreateCommentThreadMode(null);
              if (forceThreadType) onForceThreadCreated();
              if (activeFilters.size) activeFiltersActions.clear();
            }}
            onReceiveFile={onReceiveFile}
            autoFocus={createCommentThreadMode !== "auto"}
          />
        )}
        {orderedResolvables.map((r) => (
          <ConversationThread key={r.id} resolvable={r} bag={bag} />
        ))}
      </Col>
    </Col>
  );
};

const useOnUnmount = (props: NextConversationsProps) => {
  const propRefs = useRef(props);
  useEffect(() => {
    propRefs.current = props;
  });
  useEffect(() => {
    return () => {
      propRefs.current.onUnmount?.();
    };
  }, []);
};

type NextConversationsProps = {
  card: Card;
  notifications: Notifications;
  root: Root;
  canModify: boolean | null;
  forceThreadType: null | "review" | "block";
  onForceThreadCreated: () => void;
  onReceiveFile: (f: any) => any;
  onUnmount?: () => void;
  onCardDone?: () => void;
  status: string;
  location: Location;
  history: History;
  onEditCardClick?: () => void;
};

const Conversations = (props: NextConversationsProps) => {
  const {card} = props;

  useOnUnmount(props);

  const {isLoaded, result: resolvables} = checkIfPresent(() => {
    const res = card.$meta.find("resolvables", {$order: ["contextAsPrio", "-createdAt"]});
    res.forEach((r) => {
      r.$meta.get("isClosed", false);
      r.$meta.get("context", false);
    });
    return res;
  }, api);

  if (!isLoaded) {
    return (
      <Col align="center" justify="center" pa="32px">
        <DSSpinner size={24} />
      </Col>
    );
  }
  return (
    <>
      <RevealSpinner show={!isLoaded} size={24} />
      {isLoaded && <WithLoadedResolvables resolvables={resolvables} {...props} />}
    </>
  );
};

export default Conversations;
